/**
 * OSD Selector Component
 *
 * Allows users to select their electricity distribution operator (OSD).
 * Displays operator name, coverage regions, and available tariffs.
 *
 * @see /frontend/src/utils/osdMetadata.ts - OSD metadata
 */

import { Radio, Stack, Text, Group, Paper, Badge, Tooltip, Box } from '@mantine/core';
import {
  IconMapPin,
  IconBolt,
  IconInfoCircle,
} from '@tabler/icons-react';
import {
  OSDCode,
  OSD_CODES,
  OSD_METADATA,
  getAvailableTariffsForOSD,
} from '../../../utils/osdMetadata';

export interface OSDSelectorProps {
  /** Currently selected OSD code */
  value: OSDCode | null;
  /** Callback when OSD selection changes */
  onChange: (value: OSDCode) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Show compact version (less details) */
  compact?: boolean;
  /** Show tariff count badge */
  showTariffCount?: boolean;
  /** Label text (defaults to "Wybierz operatora sieci") */
  label?: string;
  /** Description text */
  description?: string;
}

/**
 * OSD Selector Component
 *
 * Radio group for selecting Polish electricity distribution operator.
 */
export function OSDSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
  showTariffCount = true,
  label = 'Wybierz operatora sieci',
  description,
}: OSDSelectorProps) {
  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <div>
          <Text fw={500} size="sm" mb={4}>
            {label}
          </Text>
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </div>

        <Radio.Group
          value={value || ''}
          onChange={(val) => onChange(val as OSDCode)}
        >
          <Stack gap="xs">
            {OSD_CODES.map((osdCode) => {
              const osd = OSD_METADATA[osdCode];
              const tariffs = getAvailableTariffsForOSD(osdCode);

              return (
                <Radio
                  key={osdCode}
                  value={osdCode}
                  disabled={disabled}
                  label={
                    <Group gap="xs" wrap="nowrap" style={{ width: '100%' }}>
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: osd.color,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <Group gap="xs" justify="space-between">
                          <Text size="sm" fw={500}>
                            {osd.shortName}
                          </Text>
                          {showTariffCount && (
                            <Tooltip
                              label={`Dostępne taryfy: ${tariffs.join(', ')}`}
                              withArrow
                            >
                              <Badge
                                size="xs"
                                variant="light"
                                color="gray"
                                leftSection={<IconBolt size={10} />}
                              >
                                {tariffs.length} taryf
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                        {!compact && (
                          <Group gap={4} mt={2}>
                            <IconMapPin
                              size={12}
                              style={{ opacity: 0.5, flexShrink: 0 }}
                            />
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {osd.coverage.slice(0, 3).join(', ')}
                              {osd.coverage.length > 3 && '...'}
                            </Text>
                          </Group>
                        )}
                      </div>
                    </Group>
                  }
                  styles={{
                    label: { width: '100%' },
                    body: { alignItems: 'flex-start' },
                    radio: {
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Radio.Group>

        {/* Help text */}
        <Group gap={4}>
          <IconInfoCircle size={12} style={{ opacity: 0.5 }} />
          <Text size="xs" c="dimmed">
            Twój OSD znajdziesz na fakturze za prąd
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}

/**
 * Compact OSD selector as a simple select dropdown
 */
export interface OSDSelectProps {
  /** Currently selected OSD code */
  value: OSDCode | null;
  /** Callback when OSD selection changes */
  onChange: (value: OSDCode) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

/**
 * Inline OSD indicator (read-only)
 */
export interface OSDIndicatorProps {
  /** OSD code to display */
  osd: OSDCode;
  /** Show full name or short name */
  showFullName?: boolean;
}

export function OSDIndicator({ osd, showFullName = false }: OSDIndicatorProps) {
  const metadata = OSD_METADATA[osd];

  return (
    <Group gap="xs" wrap="nowrap">
      <Box
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: metadata.color,
          flexShrink: 0,
        }}
      />
      <Text size="sm" fw={500}>
        {showFullName ? metadata.name : metadata.shortName}
      </Text>
    </Group>
  );
}

export default OSDSelector;
